import {
  type FailureKind,
  REFUSAL_CODES,
  failureKindForCode,
} from "@cyborg7/observability/failure";
import type { UiKpiFields, UiKpiSource } from "@cyborg7/observability/ui-kpi";
import { reportClientError } from "@cyborg7/observability/web";
import { isTransportFailure, type TransportError } from "$lib/core/transport-failure.js";

export type { FailureKind };

// The refusals a retry of the SAME prompt can never satisfy: the rule, the target or the plan
// says no, whatever the timing. The rest of REFUSAL_CODES (rate_limited, conflict, stale_send,
// timeline_rewinding, not_a_member, unauthenticated, not_configured, not_available, ...) can
// clear on their own or by the user's next step, so their bubble keeps Retry.
const PERMANENT_REFUSAL_CODES: ReadonlySet<string> = new Set([
  "forbidden",
  "unauthorized",
  "not_authorized",
  "no_daemon_access",
  "cross_workspace",
  "not_found",
  "already_deleted",
  "invalid",
  "invalid_request",
  "validation_error",
  "unsupported",
  "attachment_inline_rejected",
  "attachment_invalid",
  "invalid_emoji",
  "license_required",
  "upgrade_required",
  "pro_required",
  "seat_limit_reached",
  "prompt_channel_bound_session",
  // A prompt into a session that has ended (relay AGENT_SESSION_ENDED_MESSAGE): the session is gone.
  "session_ended",
]);

type Id = string | number | boolean | null | undefined;

export interface ReportOptions {
  // Set when the caller knows better than the code (an HTTP status, a close reason).
  failureKind?: FailureKind;
  // A code for an error that does not carry one (an HTTP status, a close reason).
  code?: string;
}

// The W3C trace id core/client.ts put on the request this error rejected.
function traceIdOf(err: unknown): string | undefined {
  const traceId = (err as { traceId?: unknown } | null)?.traceId;
  return typeof traceId === "string" && traceId ? traceId : undefined;
}

export function errorCodeOf(err: unknown): string | undefined {
  const code = (err as { code?: unknown } | null)?.code;
  if (typeof code === "string" && code) return code;
  // PromptNotSentError wraps the relay's error: its code lives on the cause.
  const cause = (err as { cause?: unknown } | null)?.cause;
  return cause === undefined || cause === err ? undefined : errorCodeOf(cause);
}

// UI-only refusals: the server records the same code as its own fault (a token it could not persist).
const UI_REFUSAL_CODES: ReadonlySet<string> = new Set(["refresh_failed"]);

export function isRefusalCode(code: string | undefined): boolean {
  return code !== undefined && (REFUSAL_CODES.has(code) || UI_REFUSAL_CODES.has(code));
}

export function isPermanentRefusal(code: string | undefined): boolean {
  return code !== undefined && PERMANENT_REFUSAL_CODES.has(code);
}

// The client's own transport failure, or one a wrapper carries as its cause (PromptNotSentError
// around "Not connected"), the way errorCodeOf reads a wrapped code.
function transportFailureOf(err: unknown, depth = 0): TransportError | undefined {
  if (isTransportFailure(err)) return err;
  const cause = (err as { cause?: unknown } | null)?.cause;
  if (depth >= 3 || cause === undefined || cause === err) return undefined;
  return transportFailureOf(cause, depth + 1);
}

function isOrWrapsTransportFailure(err: unknown): boolean {
  return transportFailureOf(err) !== undefined;
}

// A code decides. With none, the client's own timeout or dropped socket is transient.
function kindOf(err: unknown, code: string | undefined): FailureKind {
  if (code === undefined && isOrWrapsTransportFailure(err)) return "transient";
  if (code !== undefined && UI_REFUSAL_CODES.has(code)) return "refusal";
  return failureKindForCode(code);
}

/** Refusal/transient from the code or the client's transport error; fault otherwise. */
export function failureKindOf(err: unknown): FailureKind {
  return kindOf(err, errorCodeOf(err));
}

// Error prose can quote a person's email: it never leaves the machine (knowledge/123 R9).
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

function redact(text: string): string {
  return text.replace(EMAIL_RE, "<email>");
}

function messageOf(err: unknown): string {
  if (err instanceof Error) return redact(err.message);
  const message = (err as { message?: unknown } | null)?.message;
  return redact(typeof message === "string" ? message : String(err));
}

// The UI's one sink for client-side failures: ids, enums and codes only, never content,
// with the failure kind.
export function reportUiFailure(
  source: string,
  err: unknown,
  ids: Record<string, Id> = {},
  options: ReportOptions = {},
): void {
  try {
    const code = options.code ?? errorCodeOf(err);
    const fields: Record<string, Id> = {};
    for (const [key, value] of Object.entries(ids)) if (value !== undefined) fields[key] = value;
    reportClientError({
      ...fields,
      source,
      message: messageOf(err),
      // The stack's first line repeats the message, so it is redacted the same way.
      stack: err instanceof Error && err.stack ? redact(err.stack) : undefined,
      errorName: err instanceof Error ? err.name : typeof err,
      // With no server code, the client's own transport failure names itself (request_timed_out, …).
      code: code ?? transportFailureOf(err)?.transport,
      trace_id: traceIdOf(err),
      failure_kind: options.failureKind ?? kindOf(err, code),
    });
  } catch {
    // intentional: reporting a failure must never become a second failure in the UI
  }
}

export type SaveIds = Partial<Record<"workspaceId" | "taskId" | "pageId" | "projectId", string>>;

// A user write that failed (or was not applied): scope `save.<entity>`, the field NAMES that
// were being written (never values), the ids, and the error's code for refusal/fault.
export function reportSaveFailure(
  entity: string,
  fields: readonly string[],
  err: unknown,
  ids: SaveIds = {},
  options: ReportOptions = {},
): void {
  reportUiFailure(`save.${entity}`, err, { ...ids, fields: fields.join(",") }, options);
}

const lastReportAt = new Map<string, number>();

// A counted outcome, not a failure: client-log turns an allowlisted `source` into a KPI and
// never into an Error Tracking issue. Ids, enums and counters only.
export function reportUiKpi(source: string, fields: Record<string, Id>): void {
  try {
    reportClientError({ ...fields, source, message: source, failure_kind: "expected" });
  } catch {
    // intentional: a KPI report must never become a failure in the UI
  }
}

// A catalog KPI (@cyborg7/observability/ui-kpi): the source and its fields are typed from the one
// catalog the relay validates with, so a new surface needs no client-log change.
export function reportKpi<S extends UiKpiSource>(
  source: S,
  fields: UiKpiFields<S> & { workspaceId: string },
): void {
  // cast: a catalog field is a count or an enum string, both an Id; TS cannot see it through S.
  reportUiKpi(source, fields as Record<string, Id>);
}

// At most one report per `key` per `intervalMs` (polled ticks, retry storms). Claims the
// slot when true: `if (shouldReport(key, ms)) reportUiFailure(...)`.
export function shouldReport(key: string, intervalMs: number): boolean {
  const now = Date.now();
  const last = lastReportAt.get(key);
  if (last !== undefined && now - last < intervalMs) return false;
  lastReportAt.set(key, now);
  return true;
}

/** Test-only: forget every throttle window. */
export function resetUiFailureThrottle(): void {
  lastReportAt.clear();
}
